// src/components/admin/components/MusicPlayer.jsx
import { Play, Pause, X } from 'lucide-react';
import { motion } from 'framer-motion';

export function MusicPlayer({ track, isPlaying, currentTime, duration, onTogglePlay, onSeek, onClose, formatTime }) {
  if (!track) return null;

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 shadow-2xl z-40"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center gap-3 md:gap-4 mb-3">
          {/* Track Info */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate text-sm md:text-base">{track.name}</p>
            <p className="text-xs md:text-sm text-purple-100 truncate">{track.artist}</p>
          </div>

          {/* Player Controls */}
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            {/* Play/Pause Button */}
            <button
              onClick={onTogglePlay}
              className="bg-white/20 hover:bg-white/30 p-1.5 md:p-2 rounded-full transition"
            >
              {isPlaying ? <Pause size={18} className="md:w-5 md:h-5" /> : <Play size={18} className="md:w-5 md:h-5" />}
            </button>

            {/* Time Display */}
            <div className="text-xs md:text-sm font-medium w-10 md:w-12 text-right">
              {formatTime(currentTime)}
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="hover:bg-white/20 p-1 rounded transition"
            >
              <X size={18} className="md:w-5 md:h-5" />
            </button>
          </div>
        </div>

        {/* Large Progress Bar */}
        <div className="w-full h-2 md:h-3 bg-white/30 rounded-full cursor-pointer group relative">
          <motion.div
            className="h-full bg-white rounded-full"
            style={{ width: `${progress}%` }}
            transition={{ type: 'tween', duration: 0.1 }}
          />
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={(e) => onSeek(parseFloat(e.target.value))}
            className="absolute inset-0 w-full opacity-0 cursor-pointer"
          />
          {/* Hover indicator dot */}
          <motion.div
            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ left: `${progress}%`, marginLeft: '-8px' }}
          />
        </div>

        {/* Time info below progress bar */}
        <div className="flex justify-between items-center mt-2 text-xs md:text-sm font-medium text-purple-100">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
    </motion.div>
  );
}